/**
 * SickDayPanel — iter1050 (Nephro1, Фаза 2)
 * „Днес съм болен“ на едно докосване: кои лекарства да обсъдя за пауза,
 * как да се рехидратирам и кога да търся помощ.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2, ShieldAlert, Droplet, PhoneCall } from "lucide-react";

export const SickDayPanel = ({ isDark, onChanged }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sym, setSym] = useState([]);

  const apply = (d) => {
    setPlan(d);
    setSym((d.episode && d.episode.symptoms) || []);
  };

  useEffect(() => {
    axios.get(`${API}/renal/sick-day`, { timeout: 25000 })
      .then(({ data }) => apply(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const toggle = (k) => setSym((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/renal/sick-day`, { symptoms: sym });
      apply(data);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const end = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/renal/sick-day/end`, {});
      apply(data);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-3" data-testid="sick-day-loading">
      <Loader2 className="w-4 h-4 animate-spin text-rose-500" />
    </div>;
  }
  if (!plan) return null;

  const box = isDark ? "bg-slate-900/60" : "bg-white border border-slate-200";

  return (
    <div className={`mt-2 p-3 rounded-xl border-2 ${
      plan.active
        ? (isDark ? "bg-amber-900/25 border-amber-500/60" : "bg-amber-50 border-amber-300")
        : (isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200")
    }`} data-testid="sick-day-panel">
      <div className="flex items-start gap-2 mb-2">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-bold">
            {plan.active ? "Активен болен ден" : "Болен ден — какво да обсъдя"}
          </div>
          <p className="text-[11px] opacity-85 mt-0.5" data-testid="sick-day-headline">
            {plan.headline_bg}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {(plan.symptom_options || []).map((s) => (
          <button key={s.key} onClick={() => toggle(s.key)}
                  data-testid={`sick-symptom-${s.key}`}
                  className={`px-2 py-1 rounded-full text-[10px] font-semibold transition-colors ${
                    sym.includes(s.key)
                      ? "bg-rose-500 text-white"
                      : (isDark ? "bg-slate-800 text-slate-300" : "bg-white border border-slate-300 text-slate-600")
                  }`}>
            {s.label_bg}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-2">
        <button onClick={start} disabled={busy} data-testid="sick-day-start"
                className="flex-1 py-2 rounded-lg bg-rose-600 text-white text-[11px] font-bold disabled:opacity-60">
          {busy ? "Записвам…" : plan.active ? "Обнови симптомите" : "Отбележи болен ден"}
        </button>
        {plan.active && (
          <button onClick={end} disabled={busy} data-testid="sick-day-end"
                  className={`px-3 py-2 rounded-lg text-[11px] font-bold ${
                    isDark ? "bg-slate-800 text-slate-200" : "bg-white border border-slate-300"
                  }`}>
            Приключи
          </button>
        )}
      </div>

      {(plan.pause_candidates || []).length > 0 ? (
        <div className={`p-2.5 rounded-lg text-[10px] mb-2 ${box}`} data-testid="sick-day-pause-list">
          <div className="font-bold opacity-80 mb-1">
            Лекарства за обсъждане с лекар (по приоритет)
          </div>
          {plan.pause_candidates.map((m, i) => (
            <div key={`${m.name}-${i}`} className="mb-1.5" data-testid={`sick-day-pause-${i}`}>
              <strong>{m.name}</strong> <span className="opacity-70">({m.group_bg})</span>
              <div className="opacity-90">{m.action_bg}</div>
              <div className="opacity-70 italic">{m.why_bg}</div>
            </div>
          ))}
        </div>
      ) : (
        <button onClick={() => navigate("/dashboard/medications")}
                data-testid="sick-day-add-meds"
                className={`w-full text-left p-2 rounded-lg text-[10px] mb-2 ${
                  isDark ? "bg-cyan-500/10 hover:bg-cyan-500/20" : "bg-cyan-50 hover:bg-cyan-100"
                }`}>
          <span className="font-bold">Лекарства →</span>
          <span className="opacity-80"> Добавете списъка си, за да се направи личният план за болни дни.</span>
        </button>
      )}

      <div className={`p-2.5 rounded-lg text-[10px] mb-2 ${box}`} data-testid="sick-day-hydration">
        <div className="font-bold opacity-80 mb-1 flex items-center gap-1">
          <Droplet className="w-3 h-3 text-cyan-500" /> Рехидратация с числа
        </div>
        {(plan.hydration || []).map((h) => <div key={h} className="mb-0.5">• {h}</div>)}
      </div>

      <div className={`p-2.5 rounded-lg text-[10px] mb-2 ${
        isDark ? "bg-rose-900/30 border border-rose-500/50" : "bg-rose-50 border border-rose-300"
      }`} data-testid="sick-day-seek-help">
        <div className="font-bold opacity-90 mb-1 flex items-center gap-1">
          <PhoneCall className="w-3 h-3 text-rose-500" /> Кога се търси помощ веднага
        </div>
        {(plan.seek_help || []).map((h) => <div key={h} className="mb-0.5">• {h}</div>)}
      </div>

      <div className={`p-2 rounded-lg text-[10px] ${box}`} data-testid="sick-day-recheck">
        <strong>Контрол след това:</strong> {plan.recheck_bg}
      </div>
      <p className="text-[10px] italic opacity-70 mt-2" data-testid="sick-day-disclaimer">
        {plan.disclaimer_bg}
      </p>
    </div>
  );
};

export default SickDayPanel;
